<script setup lang="ts">
import { h, type VNode, type VNodeChild } from "vue";
import { ImageIcon } from "lucide-vue-next";
import type {
  Participant,
  RichTextDocument,
  RichTextMark,
  RichTextNode,
} from "@/types/models";
import { richTextOrPlain } from "@/utils/rich-text";

const props = withDefaults(
  defineProps<{
    document?: RichTextDocument;
    plainText?: string;
    compact?: boolean;
    participants?: Participant[];
  }>(),
  {
    document: undefined,
    plainText: "",
    compact: false,
    participants: () => [],
  },
);

function applyMarks(content: string, marks: RichTextMark[] = []) {
  return marks.reduce<string | VNode>((wrapped, mark) => {
    if (mark.type === "bold") return h("strong", {}, [wrapped]);
    if (mark.type === "italic") return h("em", {}, [wrapped]);
    if (mark.type === "underline") return h("u", {}, [wrapped]);
    if (mark.type === "link" && mark.attrs?.href)
      return h(
        "a",
        {
          href: mark.attrs.href,
          target: "_blank",
          rel: "noopener noreferrer",
        },
        [wrapped],
      );
    return wrapped;
  }, content);
}

function renderChildren(node: RichTextNode): VNodeChild[] {
  return (node.content || []).map((child) => renderNode(child));
}

function renderNode(node: RichTextNode): VNodeChild {
  if (node.type === "text")
    return applyMarks(node.text || "", node.marks || []);
  if (node.type === "hardBreak") return h("br");
  if (node.type === "mention")
    return h(
      "span",
      {
        class: "rich-mention",
        "data-mention-id": node.attrs?.id,
      },
      `@${node.attrs?.label || "Project participant"}`,
    );
  if (node.type === "secureImage")
    return h("figure", { class: "rich-image" }, [
      h("span", { class: "rich-image__icon", "aria-hidden": "true" }, [
        h(ImageIcon, { size: 20 }),
      ]),
      h("figcaption", [
        h("strong", node.attrs?.alt || "Pasted image"),
        h("span", [
          node.attrs?.state === "blocked"
            ? "This image is not available."
            : "Available from this work item’s Files section.",
        ]),
      ]),
    ]);

  const children = renderChildren(node);
  if (node.type === "paragraph") return h("p", children);
  if (node.type === "heading")
    return h(node.attrs?.level === 3 ? "h3" : "h2", children);
  if (node.type === "bulletList") return h("ul", children);
  if (node.type === "orderedList")
    return h("ol", { start: node.attrs?.start || 1 }, children);
  if (node.type === "listItem") return h("li", children);
  if (node.type === "table")
    return h("div", { class: "rich-table-scroll", tabindex: "0" }, [
      h("table", [h("tbody", children)]),
    ]);
  if (node.type === "tableRow") return h("tr", children);
  if (node.type === "tableHeader")
    return h(
      "th",
      {
        colspan: node.attrs?.colspan || 1,
        rowspan: node.attrs?.rowspan || 1,
        scope: "col",
      },
      children,
    );
  if (node.type === "tableCell")
    return h(
      "td",
      {
        colspan: node.attrs?.colspan || 1,
        rowspan: node.attrs?.rowspan || 1,
      },
      children,
    );
  return h("div", children);
}

function renderDocument() {
  const document = richTextOrPlain(props.document, props.plainText);
  if (!props.document && props.participants.length) {
    const people = [...props.participants].sort(
      (left, right) => right.name.length - left.name.length,
    );
    const visit = (node: RichTextNode) => {
      if (node.type === "paragraph" && node.content?.length === 1) {
        const child = node.content[0];
        if (child?.type === "text" && child.text) {
          const matches = people
            .map((person) => ({
              person,
              index: child.text!.indexOf(`@${person.name}`),
            }))
            .filter((entry) => entry.index >= 0)
            .sort((left, right) => left.index - right.index);
          if (matches.length) {
            const content: RichTextNode[] = [];
            let cursor = 0;
            for (const match of matches) {
              if (match.index < cursor) continue;
              if (match.index > cursor)
                content.push({
                  type: "text",
                  text: child.text.slice(cursor, match.index),
                });
              content.push({
                type: "mention",
                attrs: { id: match.person.id, label: match.person.name },
              });
              cursor = match.index + match.person.name.length + 1;
            }
            if (cursor < child.text.length)
              content.push({ type: "text", text: child.text.slice(cursor) });
            node.content = content;
          }
        }
      }
      node.content?.forEach(visit);
    };
    visit(document.doc);
  }
  return renderChildren(document.doc);
}
</script>

<template>
  <div class="rich-text" :class="{ 'rich-text--compact': compact }">
    <component :is="() => renderDocument()" />
  </div>
</template>

<style scoped>
.rich-text {
  color: var(--text);
  line-height: 1.58;
  overflow-wrap: anywhere;
}
.rich-text :deep(p),
.rich-text :deep(ul),
.rich-text :deep(ol),
.rich-text :deep(h2),
.rich-text :deep(h3),
.rich-text :deep(figure),
.rich-text :deep(.rich-table-scroll) {
  margin: 0 0 12px;
}
.rich-text :deep(> :last-child) {
  margin-bottom: 0;
}
.rich-text :deep(h2) {
  margin-top: 20px;
  font-size: 17px;
  line-height: 1.3;
}
.rich-text :deep(h3) {
  margin-top: 16px;
  font-size: 15px;
  line-height: 1.35;
}
.rich-text :deep(ul),
.rich-text :deep(ol) {
  padding-left: 24px;
}
.rich-text :deep(li + li) {
  margin-top: 4px;
}
.rich-text :deep(a) {
  color: var(--shared);
  font-weight: 600;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
}
.rich-text :deep(a:hover) {
  color: #164f82;
}
.rich-text :deep(.rich-mention) {
  display: inline-block;
  margin-inline: 1px;
  padding: 1px 5px;
  border-radius: var(--radius-sm);
  background: var(--shared-soft);
  color: #164f82;
  font-weight: 700;
  white-space: nowrap;
}
.rich-table-scroll {
  max-width: 100%;
  overflow-x: auto;
  border: 1px solid var(--rule-strong);
  border-radius: var(--radius-sm);
}
.rich-table-scroll:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--focus) 22%, transparent);
  outline-offset: 2px;
}
.rich-text :deep(table) {
  width: 100%;
  min-width: 420px;
  border-collapse: collapse;
}
.rich-text :deep(th),
.rich-text :deep(td) {
  min-width: 110px;
  padding: 8px 10px;
  border-right: 1px solid var(--rule);
  border-bottom: 1px solid var(--rule);
  text-align: left;
  vertical-align: top;
}
.rich-text :deep(th) {
  background: var(--sheet-strong);
  font-size: 12px;
}
.rich-text :deep(tr:last-child > *) {
  border-bottom: 0;
}
.rich-text :deep(tr > *:last-child) {
  border-right: 0;
}
.rich-text :deep(.rich-image) {
  display: grid;
  grid-template-columns: 34px 1fr;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid var(--rule-strong);
  border-radius: var(--radius-sm);
  background: var(--sheet-subtle);
}
.rich-text :deep(.rich-image__icon) {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border-radius: var(--radius-sm);
  background: var(--warning-soft);
  color: var(--warning);
}
.rich-text :deep(.rich-image figcaption) {
  min-width: 0;
  display: grid;
  gap: 2px;
}
.rich-text :deep(.rich-image figcaption span) {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--text-soft);
  font-size: 12px;
  overflow-wrap: anywhere;
}
.rich-text--compact :deep(p),
.rich-text--compact :deep(ul),
.rich-text--compact :deep(ol),
.rich-text--compact :deep(figure),
.rich-text--compact :deep(.rich-table-scroll) {
  margin-bottom: 8px;
}
</style>
